import {
  userErrorMessage,
  classroomSgfFileSchema,
  classroomSgfImportResponseSchema,
  foxKifuAiDefaultResponseSchema,
  foxKifuAiAnalysisResponseSchema,
  foxKifuAiStatusResponseSchema,
  foxKifuAiVariationResponseSchema,
  foxKifuRecordResponseSchema,
  foxKifuResearchResponseSchema,
  foxKifuSearchResponseSchema,
  type BoardPoint,
  type FoxKifuAiAnalysis,
  type FoxKifuAiAnalysisResponse,
  type FoxKifuAiPublicProfile,
  type FoxKifuAiVariationData,
  type FoxKifuClassroomResearchBody,
  type FoxKifuRecordData,
  type FoxKifuResearchData,
  type FoxKifuSearchData,
  type ClassroomSgfData,
} from '@wx-weiqi/contracts';

import { nextRequestId, WebApiError } from '../services/http';
import {
  AI_ANALYSIS_MAX_BOUNDED_RETRIES,
  AI_ANALYSIS_POLL_INTERVAL_MS,
  AI_ANALYSIS_RUNNING_STALE_MS,
  AI_ANALYSIS_SLICE_RESTART_MS,
  aiRetryDelay,
  isRetryableAiError,
  waitForAi,
} from './ai-analysis-recovery';
import type { IdentityStore } from './identity-store';
import { selectionFromStatus, type AiAccountSelectionState } from './ai-account-selection';

export interface FoxKifuState extends AiAccountSelectionState {
  readonly search: FoxKifuSearchData | null;
  readonly detailSearch: FoxKifuSearchData | null;
  readonly record: FoxKifuRecordData | null;
  readonly research: FoxKifuResearchData | null;
  readonly aiServiceEnabled: boolean | null;
  readonly aiAvailable: boolean | null;
  readonly aiConfigured: boolean | null;
  readonly aiAuthorized: boolean | null;
  readonly aiProfile: FoxKifuAiPublicProfile | null;
  readonly aiAnalysis: FoxKifuAiAnalysis | null;
  readonly aiVariation: FoxKifuAiVariationData | null;
  readonly aiVariationPending: boolean;
  readonly aiPending: boolean;
  readonly aiError: string | null;
  readonly pending: 'SEARCH' | 'DETAILS' | 'RESEARCH' | string | null;
  readonly error: string | null;
}

export interface FoxKifuSearchInput {
  readonly query: string;
  readonly from: string;
  readonly to: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export const MAX_CLASSROOM_SGF_FILES = 20;

export interface ClassroomSgfImportFailure {
  readonly filename: string;
  readonly message: string;
}

export interface ClassroomSgfBatch {
  readonly records: readonly ClassroomSgfData[];
  readonly failures: readonly ClassroomSgfImportFailure[];
}

type Listener = () => void;

export class FoxKifuStore {
  private stateValue: FoxKifuState = {
    search: null,
    detailSearch: null,
    record: null,
    research: null,
    aiServiceEnabled: null,
    aiAvailable: null,
    aiConfigured: null,
    aiAuthorized: null,
    aiProfile: null,
    aiAnalysis: null,
    aiVariation: null,
    aiVariationPending: false,
    aiPending: false,
    aiError: null,
    pending: null,
    error: null,
  };
  private readonly listeners = new Set<Listener>();
  private aiGeneration = 0;
  private aiStatusGeneration = 0;
  private classroomResearchGeneration = 0;
  private classroomSgfGeneration = 0;
  private activeAnalysisId: string | null = null;

  constructor(private readonly identity: Pick<IdentityStore, 'authorized'>) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): FoxKifuState => this.stateValue;

  async refreshAiStatus(): Promise<void> {
    const generation = ++this.aiStatusGeneration;
    try {
      const response = await this.identity.authorized(
        '/fox-kifu/ai/status',
        foxKifuAiStatusResponseSchema,
      );
      if (generation !== this.aiStatusGeneration) return;
      this.update({
        ...this.stateValue,
        aiServiceEnabled: response.data.enabled,
        aiAvailable: response.data.enabled && response.data.available,
        aiConfigured: response.data.configured,
        aiAuthorized: response.data.authorized,
        ...selectionFromStatus(this.stateValue, response.data),
        aiError: null,
      });
    } catch {
      if (generation !== this.aiStatusGeneration) return;
      this.update({
        ...this.stateValue,
        aiServiceEnabled: null,
        aiAvailable: false,
        aiConfigured: null,
        aiAuthorized: null,
        aiProfile: null,
      });
    }
  }

  selectAiAccount(configurationId: string): void {
    const account = this.stateValue.aiSelection?.accounts?.find(
      (item) => item.configurationId === configurationId,
    );
    if (account === undefined) return;
    this.cancelAi();
    this.update({
      ...this.stateValue,
      aiSelectedConfigurationId: configurationId,
      aiProfile: account.profile,
    });
  }

  async saveAiDefault(): Promise<void> {
    const generation = this.aiStatusGeneration;
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    if (!configurationId) return;
    const response = await this.identity.authorized(
      '/platform/fox-kifu-ai/my-default',
      foxKifuAiDefaultResponseSchema,
      {
        method: 'PUT',
        requestId: nextRequestId('ai-default'),
        body: { configurationId, version: this.stateValue.aiSelection?.defaultVersion ?? 0 },
      },
    );
    if (generation !== this.aiStatusGeneration) return;
    const selection = this.stateValue.aiSelection;
    if (selection)
      this.update({
        ...this.stateValue,
        aiSelection: {
          ...selection,
          defaultConfigurationId: response.data.configurationId,
          defaultVersion: response.data.version,
          selectionSource: 'DEFAULT',
        },
      });
  }

  async analyze(
    recordId: string,
    baseMoveNumber: number,
    moves: readonly BoardPoint[],
  ): Promise<void> {
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    return this.analyzePosition((analysisId) =>
      this.identity.authorized(
        `/fox-kifu/records/${encodeURIComponent(recordId)}/ai-analyses`,
        foxKifuAiAnalysisResponseSchema,
        {
          method: 'POST',
          requestId: analysisId,
          body: { baseMoveNumber, moves, ...(configurationId ? { configurationId } : {}) },
        },
      ),
    );
  }

  async analyzeClassroom(position: FoxKifuClassroomResearchBody): Promise<void> {
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    return this.analyzePosition((analysisId) =>
      this.identity.authorized('/fox-kifu/classroom/ai-analyses', foxKifuAiAnalysisResponseSchema, {
        method: 'POST',
        requestId: analysisId,
        body: { ...position, ...(configurationId ? { configurationId } : {}) },
      }),
    );
  }

  private async analyzePosition(
    createAnalysis: (analysisId: string) => Promise<FoxKifuAiAnalysisResponse>,
  ): Promise<void> {
    this.cancelActiveAnalysis();
    const generation = ++this.aiGeneration;
    const continuous = this.stateValue.aiProfile?.maxVisits === 0;
    let visibleAnalysis: FoxKifuAiAnalysis | null = null;
    let retryAttempt = 0;
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: true,
      aiError: null,
    });
    try {
      while (generation === this.aiGeneration) {
        const analysisId = nextRequestId('fox-ai');
        this.activeAnalysisId = analysisId;
        let runningWithoutResultSince: number | null = null;
        try {
          let response = await createAnalysis(analysisId);
          while (generation === this.aiGeneration) {
            const analysis = response.data.analysis;
            if (analysis.status === 'SUCCEEDED') {
              this.activeAnalysisId = null;
              visibleAnalysis = analysis;
              retryAttempt = 0;
              this.update({
                ...this.stateValue,
                aiAvailable: true,
                aiAnalysis: analysis,
                aiPending: false,
                aiError: null,
              });
              if (!continuous) return;
              await waitForAi(AI_ANALYSIS_SLICE_RESTART_MS);
              break;
            }
            if (analysis.status === 'FAILED') {
              throw new WebApiError(
                analysis.errorCode ?? 'PROCESSING_FAILED',
                '智子云未能完成当前局面分析。',
                503,
              );
            }
            if (analysis.status === 'CANCELLED') {
              throw new WebApiError('PROVIDER_UNAVAILABLE', '智子云分析意外中断。', 503);
            }
            if (analysis.result !== null) {
              visibleAnalysis = analysis;
              retryAttempt = 0;
              runningWithoutResultSince = null;
            } else if (analysis.status === 'RUNNING') {
              runningWithoutResultSince ??= Date.now();
              if (Date.now() - runningWithoutResultSince >= AI_ANALYSIS_RUNNING_STALE_MS) {
                throw new WebApiError('PROVIDER_TIMEOUT', '智子云未在时限内返回推荐点。', 503);
              }
            } else {
              runningWithoutResultSince = null;
            }
            const displayed = analysis.result === null ? visibleAnalysis : analysis;
            this.update({
              ...this.stateValue,
              aiAnalysis: displayed,
              aiPending: displayed === null,
              aiError: null,
            });
            await waitForAi(AI_ANALYSIS_POLL_INTERVAL_MS);
            response = await this.identity.authorized(
              `/fox-kifu/ai-analyses/${encodeURIComponent(analysisId)}`,
              foxKifuAiAnalysisResponseSchema,
            );
          }
        } catch (error: unknown) {
          if (generation !== this.aiGeneration) return;
          this.cancelActiveAnalysis();
          retryAttempt += 1;
          if (
            !isRetryableAiError(error) ||
            (!continuous && retryAttempt > AI_ANALYSIS_MAX_BOUNDED_RETRIES)
          ) {
            throw error;
          }
          this.update({
            ...this.stateValue,
            aiAnalysis: visibleAnalysis,
            aiPending: visibleAnalysis === null,
            aiError: continuous && retryAttempt >= 3 ? '算力连接波动，正在自动重新连接…' : null,
          });
          await waitForAi(aiRetryDelay(retryAttempt));
        }
      }
    } catch (error: unknown) {
      if (generation !== this.aiGeneration) return;
      this.activeAnalysisId = null;
      this.failAi(error);
      throw error;
    }
  }

  cancelAi(): void {
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
    });
  }

  async loadAiVariation(analysisId: string, candidateRank: number): Promise<void> {
    this.update({ ...this.stateValue, aiVariation: null, aiVariationPending: true, aiError: null });
    try {
      const response = await this.identity.authorized(
        `/fox-kifu/ai-analyses/${encodeURIComponent(analysisId)}/variations/${candidateRank}`,
        foxKifuAiVariationResponseSchema,
      );
      if (this.stateValue.aiAnalysis?.analysisId !== analysisId) return;
      this.update({
        ...this.stateValue,
        aiVariation: response.data,
        aiVariationPending: false,
        aiError: null,
      });
    } catch (error: unknown) {
      this.update({
        ...this.stateValue,
        aiVariation: null,
        aiVariationPending: false,
        aiError: userErrorMessage(error, 'AI 变化图载入失败，请稍后重试。'),
      });
      throw error;
    }
  }

  clearAiVariation(): void {
    this.update({ ...this.stateValue, aiVariation: null, aiVariationPending: false });
  }

  private cancelActiveAnalysis(): void {
    const analysisId = this.activeAnalysisId;
    this.activeAnalysisId = null;
    if (analysisId === null) return;
    void this.identity
      .authorized(
        `/fox-kifu/ai-analyses/${encodeURIComponent(analysisId)}/cancel`,
        foxKifuAiAnalysisResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('fox-ai-cancel'),
        },
      )
      .catch(() => undefined);
  }

  async search(input: FoxKifuSearchInput): Promise<void> {
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
      pending: 'SEARCH',
      error: null,
    });
    try {
      const response = await this.identity.authorized(
        `/fox-kifu/search?${this.parameters(input).toString()}`,
        foxKifuSearchResponseSchema,
      );
      this.update({
        ...this.stateValue,
        search: response.data,
        detailSearch:
          input.page === undefined || input.page === 1 ? null : this.stateValue.detailSearch,
        record: input.page === undefined || input.page === 1 ? null : this.stateValue.record,
        research: null,
        aiAnalysis: null,
        aiVariation: null,
        aiVariationPending: false,
        aiPending: false,
        aiError: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error, '野狐棋谱搜索失败，请稍后重试。');
      throw error;
    }
  }

  async searchDetails(input: FoxKifuSearchInput): Promise<void> {
    this.update({ ...this.stateValue, pending: 'DETAILS', error: null });
    try {
      const response = await this.identity.authorized(
        `/fox-kifu/search?${this.parameters({ ...input, pageSize: 10 }).toString()}`,
        foxKifuSearchResponseSchema,
      );
      this.update({ ...this.stateValue, detailSearch: response.data, pending: null, error: null });
    } catch (error: unknown) {
      this.fail(error, '野狐棋谱详细列表载入失败，请稍后重试。');
      throw error;
    }
  }

  async loadRecord(recordId: string): Promise<void> {
    this.classroomSgfGeneration += 1;
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
      pending: recordId,
      error: null,
    });
    try {
      const response = await this.identity.authorized(
        `/fox-kifu/records/${encodeURIComponent(recordId)}`,
        foxKifuRecordResponseSchema,
      );
      this.update({
        ...this.stateValue,
        record: response.data,
        research: null,
        aiAnalysis: null,
        aiVariation: null,
        aiVariationPending: false,
        aiPending: false,
        aiError: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      this.fail(error, '该棋谱暂时无法载入，请稍后重试。');
      throw error;
    }
  }

  async importClassroomSgfs(files: readonly File[]): Promise<ClassroomSgfBatch | null> {
    if (this.stateValue.pending !== null || files.length === 0) return null;
    if (files.length > MAX_CLASSROOM_SGF_FILES) {
      return {
        records: [],
        failures: [
          {
            filename: '',
            message: `一次最多上传 ${MAX_CLASSROOM_SGF_FILES} 个 SGF 文件，请重新选择。`,
          },
        ],
      };
    }
    const generation = ++this.classroomSgfGeneration;
    const records: ClassroomSgfData[] = [];
    const failures: ClassroomSgfImportFailure[] = [];
    this.cancelAi();
    this.update({ ...this.stateValue, pending: 'SGF_IMPORT', error: null });
    for (const file of files) {
      if (generation !== this.classroomSgfGeneration) return null;
      if (!classroomSgfFileSchema.safeParse({ filename: file.name, size: file.size }).success) {
        failures.push({ filename: file.name, message: '请上传不超过 1MB 的 .sgf 棋谱文件。' });
        continue;
      }
      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('file', file, 'lecture.sgf');
      try {
        const response = await this.identity.authorized(
          '/fox-kifu/classroom/sgf-import',
          classroomSgfImportResponseSchema,
          { method: 'POST', formData, requestId: nextRequestId('classroom-sgf') },
        );
        if (generation !== this.classroomSgfGeneration) return null;
        records.push(response.data);
      } catch (error) {
        if (generation !== this.classroomSgfGeneration) return null;
        failures.push({
          filename: file.name,
          message:
            error instanceof WebApiError
              ? userErrorMessage(error)
              : 'SGF 棋谱上传失败，请检查文件后重试。',
        });
      }
    }
    this.update({ ...this.stateValue, pending: null });
    return { records, failures };
  }

  async research(
    recordId: string,
    baseMoveNumber: number,
    moves: readonly BoardPoint[],
  ): Promise<void> {
    this.update({ ...this.stateValue, pending: 'RESEARCH', error: null });
    try {
      const response = await this.identity.authorized(
        `/fox-kifu/records/${encodeURIComponent(recordId)}/research`,
        foxKifuResearchResponseSchema,
        {
          method: 'POST',
          body: { baseMoveNumber, moves },
        },
      );
      this.update({ ...this.stateValue, research: response.data, pending: null, error: null });
    } catch (error: unknown) {
      this.fail(error, '试下落子失败，请重新选择交叉点。');
      throw error;
    }
  }

  async researchClassroom(body: FoxKifuClassroomResearchBody): Promise<boolean> {
    const generation = ++this.classroomResearchGeneration;
    this.update({ ...this.stateValue, pending: 'RESEARCH', error: null });
    try {
      const response = await this.identity.authorized(
        '/fox-kifu/classroom/research',
        foxKifuResearchResponseSchema,
        { method: 'POST', body },
      );
      if (generation !== this.classroomResearchGeneration) return false;
      this.update({ ...this.stateValue, research: response.data, pending: null, error: null });
      return true;
    } catch (error: unknown) {
      if (generation !== this.classroomResearchGeneration) return false;
      this.fail(error, '课堂试下失败，请检查局面后重新选择交叉点。');
      throw error;
    }
  }

  clearResearch(): void {
    this.classroomSgfGeneration += 1;
    this.classroomResearchGeneration += 1;
    this.update({ ...this.stateValue, research: null, pending: null, error: null });
  }

  clearLoadedRecord(): void {
    this.classroomSgfGeneration += 1;
    this.classroomResearchGeneration += 1;
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.update({
      ...this.stateValue,
      record: null,
      research: null,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
      pending: null,
      error: null,
    });
  }

  clear(): void {
    this.classroomSgfGeneration += 1;
    this.aiStatusGeneration++;
    this.classroomResearchGeneration += 1;
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.update({
      search: null,
      detailSearch: null,
      record: null,
      research: null,
      aiServiceEnabled: null,
      aiAvailable: null,
      aiConfigured: null,
      aiAuthorized: null,
      aiProfile: null,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
      pending: null,
      error: null,
    });
  }

  clearError(): void {
    this.update({ ...this.stateValue, error: null });
  }

  clearAiError(): void {
    this.update({ ...this.stateValue, aiError: null });
  }

  private fail(error: unknown, fallback: string): void {
    const friendly =
      error instanceof WebApiError
        ? error.code === 'RESOURCE_NOT_FOUND'
          ? '没有找到该野狐账号或棋谱，请检查昵称或 UID。'
          : error.code === 'SERVICE_UNAVAILABLE'
            ? '野狐公开棋谱服务暂时不可用，请稍后重试。'
            : error.code === 'ROLE_REQUIRED'
              ? '该功能目前仅向学生账号开放。'
              : error.code === 'OCCUPIED'
                ? '该交叉点已经有棋子。'
                : error.code === 'SUICIDE'
                  ? '该变化会使己方棋块没有气，不能落子。'
                  : error.code === 'KO_RECAPTURE' || error.code === 'POSITION_REPETITION'
                    ? '该变化违反打劫或局面重复规则。'
                    : userErrorMessage(error)
        : fallback;
    this.update({ ...this.stateValue, pending: null, error: friendly });
  }

  private failAi(error: unknown): void {
    const friendly =
      error instanceof WebApiError
        ? error.code === 'SERVICE_UNAVAILABLE' || error.code === 'PROVIDER_UNAVAILABLE'
          ? '智子云算力暂时不可用，请稍后重试。'
          : error.code === 'PROVIDER_TIMEOUT'
            ? '智子云远程引擎未在时限内就绪，请让教师核对 VIP/按量算力档位。'
            : error.code === 'RESULT_INVALID'
              ? 'AI 返回结果未通过围棋规则校验。'
              : userErrorMessage(error)
        : 'AI 算力分析失败，请稍后重试。';
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: friendly,
    });
  }

  private parameters(input: FoxKifuSearchInput): URLSearchParams {
    const parameters = new URLSearchParams({
      query: input.query.trim(),
      page: String(input.page ?? 1),
      pageSize: String(input.pageSize ?? 10),
    });
    if (input.from !== '') parameters.set('from', input.from);
    if (input.to !== '') parameters.set('to', input.to);
    return parameters;
  }

  private update(state: FoxKifuState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
